import { forwardRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { cn } from '@mlm/ui/lib/cn';
import { apiErrorMessage } from '../lib/api';

/**
 * The Miracles Protocol building blocks (SUCCESS_COACH_DESIGN_SYSTEM.md): one
 * content-block pattern, one list-row pattern, flat surfaces, hairline rules,
 * mono caps labels, and gold reserved for a screen's single primary decision.
 */

type ButtonKind = 'gold' | 'outline' | 'quiet';

export const CoachButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { kind?: ButtonKind; block?: boolean }
>(({ kind = 'outline', block, className, type = 'button', ...props }, ref) => (
  <button
    ref={ref}
    type={type}
    className={cn(
      'inline-flex min-h-11 items-center justify-center gap-2 font-mono text-[0.68rem] font-medium uppercase tracking-[0.14em] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-verde disabled:cursor-not-allowed disabled:opacity-50',
      kind === 'gold' && 'bg-gold px-6 py-3 text-ink hover:bg-[#c29d4c]',
      kind === 'outline' &&
        'border border-[rgba(16,32,26,0.22)] px-5 py-2.5 text-ink hover:border-[rgba(16,32,26,0.5)]',
      kind === 'quiet' && 'min-h-0 px-0 text-sage underline underline-offset-4 hover:text-ink',
      block && 'w-full',
      className,
    )}
    {...props}
  />
));
CoachButton.displayName = 'CoachButton';

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('eyebrow', className)}>{children}</p>;
}

/** EYEBROW / Fraunces headline / short body / one action. `size="lg"` enlarges the headline on desktop. */
export function Block({
  eyebrow,
  title,
  children,
  action,
  size,
  className,
}: {
  eyebrow?: ReactNode;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  size?: 'lg';
  className?: string;
}) {
  return (
    <section className={cn('border-b border-border px-5 py-6 lg:py-8', className)}>
      {eyebrow && <Eyebrow className="mb-2">{eyebrow}</Eyebrow>}
      {title && (
        <h2
          className={cn(
            'font-display text-[1.5rem] font-semibold leading-[1.2] text-ink',
            size === 'lg' && 'lg:text-[1.875rem]',
          )}
        >
          {title}
        </h2>
      )}
      {children && (
        <div className="mt-2 text-[0.93rem] leading-relaxed text-ink/70 lg:max-w-prose">
          {children}
        </div>
      )}
      {action && <div className="mt-5">{action}</div>}
    </section>
  );
}

/**
 * A page inside the app shell. Phone: the centred 576px column. Desktop: left
 * aligned beside the sidebar, 768px by default or 1008px with `wide` (the hubs),
 * with a larger headline and an optional `aside` (a stat) at the header's right.
 */
export function Screen({
  eyebrow,
  title,
  subtitle,
  back,
  aside,
  wide,
  children,
}: {
  eyebrow?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  back?: string;
  aside?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cn('mx-auto w-full max-w-xl lg:mx-0', wide ? 'lg:max-w-[63rem]' : 'lg:max-w-3xl')}
    >
      {(title || back) && (
        <header className="border-b border-border px-5 pb-5 pt-6 lg:pb-8 lg:pt-0 xl:flex xl:items-end xl:justify-between xl:gap-12">
          <div className="min-w-0">
            {back && (
              <Link
                to={back}
                className="eyebrow mb-4 inline-flex items-center gap-1 hover:text-ink"
              >
                <ChevronLeft className="h-3.5 w-3.5" aria-hidden /> Back
              </Link>
            )}
            {eyebrow && <Eyebrow className="mb-2">{eyebrow}</Eyebrow>}
            {title && (
              <h1 className="font-display text-[1.9rem] font-semibold leading-[1.1] text-ink lg:text-[2.75rem] lg:leading-[1.08]">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground lg:text-[0.95rem]">
                {subtitle}
              </p>
            )}
          </div>
          {aside && <div className="mt-6 xl:mt-0 xl:w-[340px] xl:shrink-0">{aside}</div>}
        </header>
      )}
      {children}
    </div>
  );
}

/** The gold seal of a finished thing. `color` takes the light gold on a dark ground. */
export function GoldSeal({ size = 20, color = '#B08D3F' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-label="Certified">
      <circle cx="12" cy="12" r="10.5" stroke={color} strokeWidth="1" />
      <circle cx="12" cy="12" r="7" stroke={color} strokeWidth="0.75" strokeDasharray="1.5 2.5" />
      <path
        d="M8.5 12.2l2.3 2.3 4.5-5"
        stroke={color}
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SageCheck({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-label="Done">
      <circle cx="12" cy="12" r="10.5" stroke="#5F6A62" strokeWidth="1" />
      <path
        d="M8 12.3l2.6 2.6L16 9.4"
        stroke="#5F6A62"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function EmptyCircle({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-label="Pending">
      <circle cx="12" cy="12" r="10.5" stroke="rgba(16,32,26,0.3)" strokeWidth="1" />
    </svg>
  );
}

/** [mark] Title / META · CONTEXT  → */
export function Row({
  mark,
  title,
  meta,
  to,
  href,
  onClick,
  trailing,
}: {
  mark?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  to?: string;
  /** An outside link (the community): opens in a new tab, or the phone's browser. */
  href?: string;
  onClick?: () => void;
  trailing?: ReactNode;
}) {
  const content = (
    <>
      {mark && <span className="mt-0.5 shrink-0">{mark}</span>}
      <span className="min-w-0 flex-1">
        <span className="block text-[0.95rem] leading-snug text-ink">{title}</span>
        {meta && <span className="eyebrow mt-1 block">{meta}</span>}
      </span>
      {trailing ??
        (to || href || onClick ? (
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-sage" aria-hidden />
        ) : null)}
    </>
  );
  const cls = 'flex w-full items-start gap-3.5 border-b border-border px-5 py-4 text-left';
  if (to)
    return (
      <Link to={to} className={cn(cls, 'transition-colors hover:bg-surface')}>
        {content}
      </Link>
    );
  if (href)
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className={cn(cls, 'transition-colors hover:bg-surface')}
      >
        {content}
      </a>
    );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(cls, 'transition-colors hover:bg-surface')}
      >
        {content}
      </button>
    );
  }
  return <div className={cls}>{content}</div>;
}

export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="eyebrow block">
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-xs leading-relaxed text-muted-foreground">{hint}</p>}
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

export function Chip({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'min-h-10 border px-3.5 py-2 text-left text-sm transition-colors',
        selected
          ? 'border-verde bg-verde text-white'
          : 'border-[rgba(16,32,26,0.22)] text-ink hover:border-[rgba(16,32,26,0.5)]',
      )}
    >
      {children}
    </button>
  );
}

export function Meter({
  label,
  value,
  note,
}: {
  label: ReactNode;
  value: number;
  /** A word beside the number, e.g. today's alignment band. */
  note?: ReactNode;
}) {
  const width = Math.max(0, Math.min(100, value));
  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <span className="eyebrow">{label}</span>
        <span className="flex items-baseline gap-2">
          <span className="font-mono text-sm text-ink">{value}</span>
          {note && <span className="eyebrow text-ink/60">{note}</span>}
        </span>
      </div>
      <div className="mt-2 h-px w-full bg-[rgba(16,32,26,0.14)]">
        <div className="h-px bg-verde" style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

/** EYEBROW / large Fraunces figure with a mono label / optional hairline progress, note or detail. */
export function Stat({
  eyebrow,
  value,
  label,
  progress,
  note,
  detail,
  compact,
  className,
}: {
  eyebrow: ReactNode;
  value: ReactNode;
  label?: ReactNode;
  progress?: number;
  note?: ReactNode;
  detail?: ReactNode;
  compact?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span
          className={cn(
            'font-display font-semibold leading-none text-ink',
            compact ? 'text-[1.75rem] xl:text-[2.125rem]' : 'text-6xl lg:text-[4rem]',
          )}
        >
          {value}
        </span>
        {label && <span className="eyebrow">{label}</span>}
      </div>
      {progress !== undefined && (
        <div className="mt-4 h-px w-full bg-[rgba(16,32,26,0.14)]">
          <div
            className="h-px bg-verde"
            style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
          />
        </div>
      )}
      {note && <p className="eyebrow mt-4">{note}</p>}
      {detail && <p className="mt-3 text-sm leading-relaxed text-ink/70">{detail}</p>}
    </div>
  );
}

/**
 * Desktop hub columns (7:5 or 1:1) split by a hairline, from `xl`. Between `lg`
 * and `xl` the columns stack beside the sidebar, where two would be too narrow.
 * Cells keep their own `px-5`, so the 28px column padding lands text 48px
 * either side of the rule.
 */
export function TwoCol({
  left,
  right,
  split = '7/5',
}: {
  left: ReactNode;
  right: ReactNode;
  split?: '7/5' | '1/1';
}) {
  return (
    <div
      className={cn(
        'xl:grid',
        split === '1/1' ? 'xl:grid-cols-2' : 'xl:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]',
      )}
    >
      <div className="min-w-0 xl:border-r xl:border-border xl:pr-7">{left}</div>
      <div className="min-w-0 xl:pl-7">{right}</div>
    </div>
  );
}

/** A row of three stats divided by hairlines (desktop Achieve). */
export function StatStrip({ children }: { children: ReactNode }) {
  return (
    <div className="grid border-b border-border lg:grid-cols-3 lg:divide-x lg:divide-border">
      {children}
    </div>
  );
}

/** Cell padding for `StatStrip` children: outer text on the page margin, 40px to each rule. */
export const statCell = 'px-5 py-6 lg:px-6 lg:py-7 lg:first:pl-5 lg:last:pr-5 xl:px-10';

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex min-h-[40vh] items-center justify-center" role="status">
      <p className="eyebrow animate-pulse">{label}</p>
    </div>
  );
}

export function ErrorNote({ error, retry }: { error: unknown; retry?: () => void }) {
  return (
    <div className="px-5 py-8">
      <p className="text-sm leading-relaxed text-destructive">{apiErrorMessage(error)}</p>
      {retry && (
        <CoachButton className="mt-4" onClick={retry}>
          Try again
        </CoachButton>
      )}
    </div>
  );
}

export const inputClass =
  'w-full border border-[rgba(16,32,26,0.22)] bg-transparent px-3.5 py-2.5 text-[0.95rem] text-ink placeholder:text-sage-deep focus:border-verde focus:outline-none disabled:opacity-50';

/**
 * A password field with a reveal toggle.
 *
 * Typing a password blind on a phone keyboard is where most failed sign-ins
 * come from, so every password input in this app gets the same affordance.
 *
 * Three details that make it correct rather than decorative: the button is
 * `tabIndex={-1}` so it never sits between the field and the submit button in
 * the tab order; it announces state through `aria-pressed` and a label that
 * changes, because the icon alone tells a screen reader nothing; and the right
 * padding is reserved on the input so a long password cannot run underneath the
 * icon.
 */
export function PasswordInput({
  id,
  value,
  onChange,
  autoComplete,
  required,
  disabled,
  placeholder,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <input
        id={id}
        type={shown ? 'text' : 'password'}
        className={cn(inputClass, 'pr-11')}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-pressed={shown}
        aria-label={shown ? 'Hide password' : 'Show password'}
        onClick={() => setShown((v) => !v)}
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-sage-deep transition-colors hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-verde disabled:opacity-50"
        disabled={disabled}
      >
        {shown ? (
          <EyeOff className="h-[18px] w-[18px]" strokeWidth={1.5} aria-hidden />
        ) : (
          <Eye className="h-[18px] w-[18px]" strokeWidth={1.5} aria-hidden />
        )}
      </button>
    </div>
  );
}
